import React, { useMemo, useState } from "react";
import Logo from "components/logo";
import styled from "styled-components";
import TextInput from "components/inputs/text-input";
import PasswordInput from "components/inputs/password-input";
import AuthImage from "images/png/auth-image.png";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import PasswordCheck from "components/password-checks";
import CheckBox from "components/check-box";
import PrimaryButton from "components/buttons/primary-button";
import { Link } from "react-router-dom";
import Modal from "components/modal";
import Verify from "./verify";
import SetUpArtist from "./setupArtist";
import api from "services/axiosInstance";
import Toast from "components/toast";

const CreateAccount = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [updates, setUpdates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [artistModalOpen, setArtistModalOpen] = useState(false);

  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const ButtonAbled = useMemo(() => {
    return (
      fullName.length > 3 &&
      /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email) &&
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).*$/.test(
        password
      ) &&
      terms
    );
  }, [password, fullName, email, terms]);

  const ResendCode = async () => {
    try {
    } catch (e) {
      Toast({ error: e });
    }
  };

  const CreateAccount = async (e: any) => {
    e.preventDefault();
    if (!ButtonAbled) {
      return;
    }
    setLoading(true);

    try {
      const response = await api.post("/auth/register", {
        name: fullName,
        email,
        password,
        password_confirm: password
      });
      Toast({ type: "success", message: `An OTP has been sent to ${email}` });

      localStorage.setItem("token", response.data.accessToken);
      setVerifyModal(true);
    } catch (e) {
      Toast({ error: e });
    } finally {
      setLoading(false);
    }
  };

  const VerifyCall = async (otp: string) => {
    try {
      const res = await api.post("auth/verify-email", { token: otp });

      Toast({ response: res });
      setVerifyModal(false);
      setArtistModalOpen(true);
    } catch (e) {
      Toast({ error: e });
    } finally {
    }
  };

  const OnSetUpArtist = async () => {
    try {
      //simulate API call to set up artist

      setArtistModalOpen(false);
    } catch (e) {
    } finally {
    }
  };

  //google login
  const initiateGoogleLogin = () => {
    window.open(
      `${BaseUrl}/auth/google/login`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  //spotify login
  const SpotifyAuth = async () => {
    try {
      window.open(
        `${BaseUrl}/auth/spotify/login`,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (e) {
      Toast({ error: e });
    }
  };

  return (
    <Container>
      <Main onSubmit={CreateAccount}>
        <LogoContainer>
          <Logo width="11.8rem" variant />
        </LogoContainer>
        <Typography
          align="center"
          weight="semibold"
          size="4xl"
          mb="0.4"
          color={Colors.white100}
        >
          Welcome
        </Typography>
        <Typography align="center" mb="2" color={Colors.white200}>
          Create an account to get started
        </Typography>
        <InputItem>
          <TextInput
            label="Full Name"
            required
            placeholder="Enter your full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
          />
        </InputItem>
        <InputItem>
          <TextInput
            label="Email"
            required
            placeholder="example@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </InputItem>
        <InputItem>
          <PasswordInput
            label="Password"
            required
            placeholder="Enter Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            autoComplete="password"
          />
          <PasswordChecks>
            <PasswordCheck
              check="8 characters"
              status={password.length >= 8 ? "pass" : "default"}
            />
            <PasswordCheck
              check="uppercase"
              status={/[A-Z]/.test(password) ? "pass" : "default"}
            />
            <PasswordCheck
              check="lowercase"
              status={/[a-z]/.test(password) ? "pass" : "default"}
            />
            <PasswordCheck
              check="Number"
              status={/\d/.test(password) ? "pass" : "default"}
            />
            <PasswordCheck
              check="Special Character"
              status={
                /[!@#$%^&*(),.?":{}|<>]/.test(password) ? "pass" : "default"
              }
            />
          </PasswordChecks>
        </InputItem>
        <Checks>
          <CheckText>
            <CheckBox
              onChange={(value) => {
                setTerms(value);
              }}
            />
            <span style={{ display: "inline-flex" }}>
              <Typography size="s" ml="0.4" color={Colors.grey400}>
                By signing up, you accept Rated Afrika’s
              </Typography>
              <TextLink to={"/terms"}>
                <Typography
                  size="s"
                  ml="0.4"
                  weight="medium"
                  color={Colors.grey400}
                  decoration="underline"
                >
                  Terms & Conditions
                </Typography>
              </TextLink>
            </span>
          </CheckText>
          <CheckText>
            <CheckBox
              onChange={(value) => {
                setUpdates(value);
              }}
            />
            <Typography ml="0.4" color={Colors.grey400}>
              I consent to receive updates & newsletters
            </Typography>
          </CheckText>
        </Checks>
        <Button>
          <PrimaryButton
            // type="submit"
            inset
            text="Create Account"
            sparkle={true}
            disabled={!ButtonAbled}
            loading={loading}
          />
        </Button>
        <OrContainer>
          <HorizontalLine />
          <Typography color={Colors.grey500} weight="medium">
            or
          </Typography>
          <HorizontalLine />
        </OrContainer>
        <SignInButtons>
          <SigninButton onClick={initiateGoogleLogin}>
            <ButtonIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="100%"
                height="100%"
                viewBox="0 0 48 48"
              >
                <path
                  fill="#fbc02d"
                  d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12	s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20	s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                ></path>
                <path
                  fill="#e53935"
                  d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039	l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                ></path>
                <path
                  fill="#4caf50"
                  d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36	c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                ></path>
                <path
                  fill="#1565c0"
                  d="M43.611,20.083L43.595,20L42,20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571	c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                ></path>
              </svg>
            </ButtonIcon>
          </SigninButton>
          <SigninButton>
            <ButtonIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12.2363 6.98601C13.016 6.98601 13.9932 6.45894 14.5752 5.75617C15.1023 5.11929 15.4866 4.22984 15.4866 3.3404C15.4866 3.21962 15.4756 3.09883 15.4537 3C14.5862 3.03294 13.543 3.58198 12.9171 4.31769C12.423 4.87771 11.9728 5.75617 11.9728 6.65659C11.9728 6.78836 11.9947 6.92013 12.0057 6.96405C12.0606 6.97503 12.1485 6.98601 12.2363 6.98601ZM9.49113 20.2727C10.5563 20.2727 11.0284 19.559 12.3571 19.559C13.7077 19.559 14.0042 20.2508 15.1901 20.2508C16.3541 20.2508 17.1337 19.1747 17.8694 18.1205C18.693 16.9126 19.0334 15.7267 19.0554 15.6718C18.9785 15.6498 16.7494 14.7384 16.7494 12.1799C16.7494 9.9618 18.5063 8.96255 18.6052 8.88568C17.4412 7.21661 15.6733 7.17269 15.1901 7.17269C13.8834 7.17269 12.8183 7.9633 12.1485 7.9633C11.4237 7.9633 10.4684 7.21661 9.3374 7.21661C7.18517 7.21661 5 8.99549 5 12.3556C5 14.4419 5.81258 16.6491 6.81182 18.0766C7.66832 19.2845 8.41501 20.2727 9.49113 20.2727Z"
                  fill="white"
                />
              </svg>
            </ButtonIcon>
          </SigninButton>
          <SigninButton onClick={SpotifyAuth}>
            <ButtonIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 25 24"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M17.2322 11.0922C14.6535 9.56079 10.4 9.41991 7.9382 10.1671C7.54289 10.2871 7.12485 10.0639 7.00506 9.66857C6.88526 9.27302 7.10824 8.85522 7.5039 8.73507C10.3297 7.87732 15.0273 8.04293 17.996 9.8052C18.3516 10.0163 18.4683 10.4756 18.2575 10.8305C18.0465 11.1861 17.587 11.3033 17.2322 11.0922ZM17.1478 13.3605C16.967 13.654 16.5831 13.746 16.2899 13.5658C14.14 12.2443 10.8618 11.8615 8.31819 12.6336C7.98838 12.7332 7.63995 12.5473 7.53983 12.218C7.44041 11.8882 7.62652 11.5405 7.95575 11.4401C10.8613 10.5585 14.4733 10.9855 16.9426 12.503C17.2358 12.6835 17.328 13.0675 17.1478 13.3605ZM16.1689 15.5388C16.0251 15.7745 15.7182 15.8483 15.4833 15.7048C13.6048 14.5565 11.2401 14.2973 8.45542 14.9333C8.18709 14.9948 7.91959 14.8266 7.85845 14.5584C7.79697 14.2901 7.96446 14.0226 8.2335 13.9613C11.2809 13.2647 13.8949 13.5645 16.0035 14.8531C16.2386 14.9966 16.3127 15.3038 16.1689 15.5388ZM12.5 4C8.08178 4 4.5 7.58167 4.5 11.9999C4.5 16.4186 8.08178 20 12.5 20C16.9183 20 20.5 16.4186 20.5 11.9999C20.5 7.58167 16.9183 4 12.5 4Z"
                  fill="#1ED760"
                />
              </svg>
            </ButtonIcon>
          </SigninButton>
        </SignInButtons>
        <FooterContainer>
          <span style={{ display: "inline-flex" }}>
            <Typography mr="0.5" size="s" color={Colors.grey500}>
              Already have an account?{" "}
            </Typography>
            <TextLink to={"/login"}>
              <Typography
                color={Colors.yellow400}
                size="s"
                decoration="underline"
              >
                Log in
              </Typography>
            </TextLink>
          </span>
        </FooterContainer>
      </Main>
      <ImageContainer src={AuthImage} alt="auth image" />
      {verifyModal && (
        <Modal isActive={verifyModal} closeModal={() => setVerifyModal(false)}>
          <Verify
            email={email}
            close={() => setVerifyModal(false)}
            confirm={(otp) => VerifyCall(otp)}
            resend={ResendCode}
          />
        </Modal>
      )}
      {artistModalOpen && (
        <Modal
          isActive={artistModalOpen}
          closeModal={() => setArtistModalOpen(false)}
        >
          <SetUpArtist
            close={() => setArtistModalOpen(false)}
            confirm={OnSetUpArtist}
          />
        </Modal>
      )}
    </Container>
  );
};

export default CreateAccount;

const Container = styled.main`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media only screen and (max-width: 769px) {
    height: 100vh;
  }
`;

const Main = styled.form`
  height: 100%;
  width: 50%;
  padding: 5rem 10.4rem;
  @media only screen and (max-width: 769px) {
    width: 100%;
    padding: 7rem 2.4rem;
  }
`;

const LogoContainer = styled.div`
  font-family: "Aristotelica Display Trial", sans-serif;
  font-family: "Aristotelica Small Caps", sans-serif;
  margin-bottom: 1.5rem;
  margin-left: auto;
  margin-right: auto;
  width: fit-content;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const InputItem = styled.div`
  margin-bottom: 1rem;
`;

const PasswordChecks = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: start;
  margin-top: 0.8rem;
  row-gap: 1rem;
`;

const Checks = styled.div`
  margin-top: 2rem;
`;
const CheckText = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const TextLink = styled(Link)`
  all: unset;
  cursor: pointer;
`;
const Button = styled.div`
  width: 100%;

  margin-top: 1.5rem;
`;

const OrContainer = styled.div`
  width: 100%;
  margin-top: 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HorizontalLine = styled.div`
  width: 45%;
  height: 1px;
  border-top: 1px solid ${Colors.grey600};
`;
const SignInButtons = styled.div`
  width: 40%;
  margin-top: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  margin-left: auto;
  margin-right: auto;
`;

const SigninButton = styled.div`
  padding: 0.8rem;
  width: fit-content;
  border: 1px solid ${Colors.grey600};
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.grey400};
  cursor: pointer;
`;
const ButtonIcon = styled.div`
  width: 2.4rem;
`;

const FooterContainer = styled.div`
  margin-top: 2rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ImageContainer = styled.img`
  width: 50%;
  height: 100vh;
  max-height: 1000px;
  object-fit: cover;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;
